// Block K (UI-20): SEAT-5's break-glass ownership transfer (super_admin), as the mockup's steps:
// 1 the business, 2 the new owner, 3 the safety checks, 4 TRANSFER typed in full (decision 53:
// no Undo, so the word is typed). Same endpoints and the same order as before; the engine
// (lib/ownershipTransfer.js) re-runs every check on Start, so the screen's checks are a preview.
//
// Fixed (AdminPanel.test.js): a refusal read "Request failed with status 409" because the routes
// answer 409 with { reason, detail } and api() throws on it; a failed job list read "No transfer
// jobs" (its error was swallowed); window.confirm is gone; changing an ID clears the old checks.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/formatting';
import { Banner, Button, Card, ConfirmDialog, EmptyState, ErrorState, Field, Input, Skeleton, StatusPill } from '../../ui';
import { checkStates, transferFailure, transferState, reasonText } from './adminLabels';

const CHECK_MARK = { passed: '✓', failed: '!', 'not-reached': '–' };
const CHECK_WORD = { passed: 'Passed', failed: 'Failed', 'not-reached': 'Not checked' };

export default function OwnershipTransfer() {
  const [businessId, setBusinessId] = useState('');
  const [newOwnerId, setNewOwnerId] = useState('');
  const [precheck, setPrecheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [typed, setTyped] = useState('');
  const [jobs, setJobs] = useState({ status: 'loading', list: [], error: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { tone, text }
  const [aborting, setAborting] = useState(null);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api('/api/admin/business-transfer');
      const d = await r.json();
      setJobs({ status: 'ready', list: Array.isArray(d?.jobs) ? d.jobs : [], error: '' });
    } catch (err) {
      setJobs((j) => ({ ...j, status: 'failed', error: messageFromError(err, 'The transfer jobs could not be loaded.') }));
    }
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // A changed ID makes the old checks about something else.
  const edit = (setter) => (e) => { setter(e.target.value); setPrecheck(null); setTyped(''); setResult(null); };

  async function runChecks() {
    setChecking(true); setResult(null); setPrecheck(null); setTyped('');
    try {
      const r = await api('/api/admin/business-transfer/precheck', { method: 'POST', body: JSON.stringify({ businessId: businessId.trim(), newOwnerId: newOwnerId.trim() }) });
      setPrecheck(await r.json());
    } catch (err) {
      setResult({ tone: 'problem', text: messageFromError(err, 'The safety checks could not run.') });
    } finally { setChecking(false); }
  }

  async function start() {
    if (typed !== 'TRANSFER' || busy) return;
    setBusy(true); setResult(null);
    try {
      const r = await api('/api/admin/business-transfer', { method: 'POST', body: JSON.stringify({ businessId: businessId.trim(), newOwnerId: newOwnerId.trim() }) });
      const d = await r.json();
      const state = d?.job?.state;
      setResult({ tone: 'success', text: `Transfer ${state ? transferState(state).label.toLowerCase() : 'started'}.${d?.awaitingPayment ? ' The new owner must add a card before it can finish.' : ''}` });
      setPrecheck(null); setTyped('');
      await loadJobs();
    } catch (err) {
      setResult({ tone: 'problem', text: `Refused. ${transferFailure(err, 'The transfer could not start.')}` });
    } finally { setBusy(false); }
  }

  async function act(job, action) {
    setBusy(true); setResult(null);
    try {
      const r = await api(`/api/admin/business-transfer/${job.id}/${action}`, { method: 'POST' });
      const d = await r.json();
      setResult({ tone: 'success', text: `Job ${transferState(d?.job?.state || '').label.toLowerCase()}.` });
      await loadJobs();
    } catch (err) {
      const text = transferFailure(err, `The ${action} did not work.`);
      if (action === 'abort') throw new Error(text);   // ConfirmDialog shows it inside the dialog
      setResult({ tone: 'problem', text });
    } finally { setBusy(false); }
  }

  const checks = checkStates(precheck);
  const canCheck = businessId.trim() && newOwnerId.trim() && !checking;

  return (
    <div className="adm-stack">
      <Card color="pastel-coral" label="Break-glass: ownership transfer" lift={false}>
        <p className="adm-muted adm-lead">
          Moves a business to a new owner. Their current subscription is cancelled and a new one starts under the new owner,
          because Stripe cannot move a subscription between customers.
        </p>
        <ol className="adm-steps">
          <li className="adm-step">
            <span className="adm-step__title">1. Which business?</span>
            <Field label="Business ID">
              <Input value={businessId} onChange={edit(setBusinessId)} placeholder="Business ID (UUID)" autoComplete="off" spellCheck={false} />
            </Field>
          </li>
          <li className="adm-step">
            <span className="adm-step__title">2. Who should own it?</span>
            <Field label="New owner's user ID" hint="They must already be a member of the business.">
              <Input value={newOwnerId} onChange={edit(setNewOwnerId)} placeholder="New owner user ID (a member)" autoComplete="off" spellCheck={false} />
            </Field>
            <div><Button variant="secondary" onClick={runChecks} disabled={!canCheck} aria-busy={checking || undefined}>{checking ? 'Checking…' : 'Run the safety checks'}</Button></div>
          </li>
          {precheck && (
            <li className="adm-step">
              <span className="adm-step__title">3. Safety checks</span>
              <ul className="adm-checks" aria-label="Safety checks">
                {checks.map((c) => (
                  <li key={c.key} className={`adm-check adm-check--${c.state}`}>
                    <span className="adm-check__mark" aria-hidden="true">{CHECK_MARK[c.state]}</span>
                    <span className="adm-check__text">
                      <span>{c.label}</span>
                      <span className="adm-sr">{CHECK_WORD[c.state]}</span>
                      {c.state === 'failed' && <span className="adm-muted">{reasonText(precheck.reason)}{c.detail ? ` ${c.detail}` : ''}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {!precheck.ok && <p className="adm-muted">The transfer cannot start until the failed check is fixed.</p>}
            </li>
          )}
          {precheck?.ok && (
            <li className="adm-step">
              <span className="adm-step__title">4. Confirm</span>
              <p className="adm-muted">This cannot be undone from here. Type <strong>TRANSFER</strong> to move business <span className="adm-mono">{businessId.trim()}</span> to <span className="adm-mono">{newOwnerId.trim()}</span>.</p>
              <div className="adm-confirm">
                <Field label="Type TRANSFER to confirm">
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} />
                </Field>
                <Button variant="delete" onClick={start} disabled={typed !== 'TRANSFER' || busy} aria-busy={busy || undefined}>{busy ? 'Starting…' : 'Start transfer'}</Button>
              </div>
            </li>
          )}
        </ol>
        {result && <div className="adm-result" role={result.tone === 'problem' ? 'alert' : 'status'}><Banner tone={result.tone}>{result.text}</Banner></div>}
      </Card>

      <Card color="pastel-neutral" label="Transfer jobs" meta={jobs.status === 'ready' ? `${jobs.list.length}` : undefined} lift={false}>
        {jobs.status === 'loading' ? <Skeleton lines={2} label="Loading transfer jobs" />
          : jobs.status === 'failed' ? <ErrorState title="Transfer jobs did not load" message={jobs.error} onRetry={loadJobs} />
          : jobs.list.length === 0 ? <EmptyState icon="🔁" title="No transfers yet">A transfer you start appears here, with where it has got to.</EmptyState>
          : (
            <ul className="adm-jobs">
              {jobs.list.map((j) => {
                const st = transferState(j.state);
                return (
                  <li key={j.id} className="adm-job">
                    <div className="adm-job__head">
                      <StatusPill tone={st.tone}>{st.label}</StatusPill>
                      {j.createdAt && <span className="adm-muted">{formatDateTime(j.createdAt)}</span>}
                    </div>
                    <span className="adm-wrap">Business <span className="adm-mono">{j.businessId}</span>: <span className="adm-mono">{j.oldOwnerId}</span> → <span className="adm-mono">{j.newOwnerId}</span></span>
                    {st.help && <span className="adm-muted">{st.help}</span>}
                    {j.error && <span className="adm-error">Error: {j.error}</span>}
                    {(['awaiting_payment', 'flipped', 'failed'].includes(j.state) || ['pending', 'awaiting_payment'].includes(j.state)) && (
                      <div className="adm-row-actions">
                        {['awaiting_payment', 'flipped', 'failed'].includes(j.state) && <Button size="sm" variant="secondary" onClick={() => act(j, 'resume')} disabled={busy}>Resume</Button>}
                        {['pending', 'awaiting_payment'].includes(j.state) && <Button size="sm" variant="destructive" onClick={() => setAborting(j)} disabled={busy}>Abort</Button>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
      </Card>

      <ConfirmDialog open={!!aborting} onClose={() => setAborting(null)} onConfirm={() => act(aborting, 'abort')}
        title="Abort this transfer?" confirmLabel="Abort transfer"
        message="The new subscription that was created is cancelled and ownership stays where it is. Nothing else changes." />
    </div>
  );
}
